import React from "react";
import { Block } from "app/shared/components/blocks/block";
import { InputPinCode } from "app/shared/components/form/input/input-pin-code";
import { QueueSpacing } from "app/shared/components/spacing";
import { spacing } from "app/shared/styles/spacing";
import { useTranslation } from "react-i18next";
import ReactModal from "react-modal";
import { Pressable, StyleSheet, View, ActivityIndicator } from "react-native";
import { Dialog, Paragraph, useTheme } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import { setCardStore } from "../../reducer/actions";
import { getCardStoreSelector, getLimit4NumDigitSelector } from "../../reducer/selectors";

interface ModalInputCardProps {
  cardID: string;
  open: boolean;
  closeModal: () => void;
  acceptModal: (code: string) => void;
}

const ModalActivatedCard = (props: ModalInputCardProps) => {
  const { cardID, open, closeModal, acceptModal } = props;
  const { t } = useTranslation();
  const { colors, fonts } = useTheme();
  const dispatchStore = useDispatch();

  const cardStore = useSelector(getCardStoreSelector);
  const limitInputStore = useSelector(getLimit4NumDigitSelector);

  const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(open);
  const [code, setCode] = React.useState<string>("");
  const [disableAccept, setDisableAccept] = React.useState<boolean>(true);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [limitInput, setLimitInput] = React.useState<number>(3);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isFocus, setFocus] = React.useState<boolean>(false);

  const acceptColorStyle = { color: disableAccept ? colors.disabled : colors.accent };
  const accentColorStyle = { color: colors.accent };
  const buttonStyle = { ...fonts.medium };

  React.useEffect(() => {
    setModalIsOpen(open);
  }, [open]);

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
        },
        titleStyle: {
          letterSpacing: 0,
          textAlign: "center",
        },
        textStyle: {
          textAlign: "center",
        },
        messageStyle: {
          color: colors.blackText,
          letterSpacing: 0,
          textAlign: "center",
        },
        actionStyle: {
          justifyContent: "center",
        },
        cellStyleFocused: { justifyContent: "center", marginTop: spacing.medium },
        textErrorStyle: {
          color: colors.error,
          letterSpacing: 0,
          textAlign: "center",
          marginTop: spacing.large,
        },
      }),
    [colors.blackText, colors.error],
  );

  const onChangeCode = (value: string) => {
    if (limitInput && limitInput > 0) {
      if (Number(value).toString() === "NaN") {
        setCode(code);
      } else if (value.length <= 4 && Number(value).toString() !== "NaN") setCode(value);
      if ((value.length < 4 || Number(value).toString() === "NaN") && !disableAccept) setDisableAccept(true);
    }
  };

  const onComplete = (value: string) => {
    if (value.length === 4 && Number(value).toString() !== "NaN") setDisableAccept(false);
  };

  const initReset = () => {
    setLoading(false);
    setFocus(true);
    setErrorMessage(undefined);
  };

  const closeActivatedCard = React.useCallback(() => {
    setDisableAccept(true);
    setCode("");
    initReset();
    closeModal();
  }, [closeModal]);

  const acceptActivatedCard = () => {
    acceptModal(code);
    setDisableAccept(true);
  };

  const contentChildren = (
    <>
      {limitInput > 0 && (
        <>
          <Paragraph style={styles.messageStyle} children={t("CARD_KICH_HOAT_NHAP_4_SO_CUOI")} />
          <InputPinCode
            autoFocus={true}
            maxLength={4}
            numeric={true}
            value={code}
            onChange={onChangeCode}
            tintColor={colors.accent}
            onComplete={onComplete}
            textColor={colors.blackText}
            style={styles.cellStyleFocused}
            isFocus={isFocus}
          />
          {loading && (
            <Block mt={spacing.large}>
              <ActivityIndicator color={colors.primary} size={"small"} />
            </Block>
          )}
        </>
      )}
      <Paragraph style={styles.textErrorStyle} children={errorMessage} />
    </>
  );

  React.useEffect(() => {
    if (cardStore.statusCardConfirm === "error") {
      const count = limitInput - 1;
      let msg = "";
      if (count > 0) {
        if (cardStore.error?.length) {
          msg = cardStore.error;
          if (cardStore.error === "326") {
            setLimitInput(count);
            msg = t("CARD_THONG_BAO_LOI_KICH_HOAT_THE")
              .replace("[num1]", String(3 - count))
              .replace("[num2]", String(count));
          }
        }
        setErrorMessage(msg);
      } else {
        setLimitInput(count);
        setErrorMessage(t("CARD_THONG_BAO_LOI_KICH_HOAT_THE_LIMIT"));
        setDisableAccept(true);
      }
      setCode("");
      setFocus(true);
      dispatchStore(setCardStore({ ...cardStore, statusCardConfirm: "init" }));
    }
  }, [cardStore, cardStore.error, cardStore.statusCardConfirm, dispatchStore, limitInput, t]);

  React.useEffect(() => {
    if (modalIsOpen && cardStore.statusCardConfirm === "pending") {
      setFocus(false);
      setLoading(true);
      setErrorMessage(undefined);
    }
  }, [modalIsOpen, cardStore.statusCardConfirm]);

  React.useEffect(() => {
    if (modalIsOpen && cardStore.statusCardConfirm === "pending") {
      if (!loading) setLoading(true);
    }

    if (modalIsOpen && cardStore.statusCardConfirm !== "pending") {
      if (loading) setLoading(false);
    }
  }, [modalIsOpen, cardStore.statusCardConfirm, loading]);

  React.useEffect(() => {
    if (modalIsOpen && cardStore.statusCardConfirm === "ok") {
      initReset();
      closeActivatedCard();
    }
  }, [modalIsOpen, cardStore.statusCardConfirm, closeActivatedCard]);

  React.useEffect(() => {
    let _limit: number = 3;
    if (limitInputStore && limitInputStore.length) {
      const filters = limitInputStore.filter((item) => {
        return item.cardID === cardID;
      });
      if (filters.length) {
        const { limit } = filters[0];
        _limit = limit;
      } else _limit = 3;
    }
    setLimitInput(_limit);
  }, [cardID, limitInputStore]);

  React.useEffect(() => {
    initReset();
  }, [modalIsOpen]);

  return (
    <React.Fragment>
      <ReactModal
        style={{
          overlay: {
            zIndex: 9999,
            position: "fixed",
            backgroundColor: colors.backdrop,
          },
          content: {
            top: "50%",
            borderRadius: 24,
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "auto",
            maxWidth: "80%",
          },
        }}
        isOpen={modalIsOpen}
        onRequestClose={closeActivatedCard}
        shouldCloseOnOverlayClick={false}
        contentLabel="Modal"
        ariaHideApp={false}>
        <View style={styles.container}>
          <Dialog.Title children={t("COMMON_THONG_BAO")} style={[styles.titleStyle]} />
          <Dialog.Content children={contentChildren} />
          <Dialog.Actions style={styles.actionStyle}>
            {limitInput > 0 && (
              <Pressable onPress={acceptActivatedCard} disabled={disableAccept}>
                <Paragraph style={[acceptColorStyle, buttonStyle]} children={t("COMMON_TIEP_TUC")} />
              </Pressable>
            )}
            <QueueSpacing size={limitInput > 0 ? "massive" : "none"} />
            <Pressable onPress={closeActivatedCard}>
              <Paragraph style={[accentColorStyle, buttonStyle]} children={t("COMMON_DONG")} />
            </Pressable>
          </Dialog.Actions>
        </View>
      </ReactModal>
    </React.Fragment>
  );
};
export default ModalActivatedCard;
